import { useState } from "react";
import BusinessCenterCompanyPageView from "./BusinessCenterCompanyPageView";
import useEvaApi, { EvaApiCall } from "../../../hooks/useEvaApi";
import { useParams } from "react-router-dom";
import { useRouteContext } from "../../../context/RouteContext";
import useCustomEffect from "../../../hooks/useCustomEffect";

function BusinessCenterCompanyPage() {
  const callEvaApi = useEvaApi();
  const { business_center_company } = useParams();
  const routeContext = useRouteContext();
  const [companies, setCompanies] = useState([]);
  const [logos, setLogos] = useState([]);
  const [terminals, setTerminals] = useState([]);
  const [businessCenterCompanyData, setBusinessCenterCompanyData] = useState({
    company_id: null,
    logo_id: null,
    terminal_id: null,
  });
  const [submitError, setSubmitError] = useState(null);

  /**
   * Initializes the component
   */
  useCustomEffect(() => {
    fetchLogos();
    fetchTerminals();

    if (!business_center_company) {
      fetchCompanies();
    }
    if (business_center_company) {
      fetchBusinessCenterCompanyData();
    }
  });

  /**
   * This method will fetch the companies from the API
   */
  async function fetchCompanies() {
    await callEvaApi(
      new EvaApiCall("companies")
        .setLoadingGroup("business_center_company_page_fetch")
        .setParams({
          where: [{ column: "type", values: ["default"] }],
        })
        .setOnSuccess((response) => {
          setCompanies(response.data.data.records);
        })
        .setRedirectOnError(true)
    );
  }

  /**
   * This method will fetch the logos from the API
   */
  async function fetchLogos() {
    await callEvaApi(
      new EvaApiCall("files")
        .setLoadingGroup("business_center_company_page_fetch")
        .setParams({ where: [{ column: "type", values: ["image"] }] })
        .setOnSuccess((response) => {
          setLogos(response.data.data.records);
        })
        .setRedirectOnError(true)
    );
  }

  /**
   * This method will fetch the terminals from the API
   */
  async function fetchTerminals() {
    await callEvaApi(
      new EvaApiCall("terminals")
        .setLoadingGroup("business_center_company_page_fetch")
        .setOnSuccess((response) => {
          setTerminals(response.data.data.records);
        })
        .setRedirectOnError(true)
    );
  }

  /**
   * This method will fetch the business center company data from the API
   */
  async function fetchBusinessCenterCompanyData() {
    await callEvaApi(
      new EvaApiCall(`business_center_companies/${business_center_company}`)
        .setParams({ with_business_center_company_company_data: true })
        .setLoadingGroup("business_center_company_page_fetch")
        .setOnSuccess((response) => {
          const newData = response.data.data;

          if (business_center_company) {
            setCompanies([newData.company]);
          }

          setBusinessCenterCompanyData({
            company_id: newData.company?.id,
            logo_id: newData.logo,
            terminal_id: newData.terminal,
          });
        })
        .setRedirectOnError(true)
    );
  }

  /**
   * This method will update the company_id on input change
   * @param {any} value
   */
  function onCompanyIdChange(value) {
    setBusinessCenterCompanyData({
      ...businessCenterCompanyData,
      company_id: value,
    });
  }

  /**
   * This method will update the logo_id on input change
   * @param {any} value
   */
  function onLogoIdChange(value) {
    setBusinessCenterCompanyData({
      ...businessCenterCompanyData,
      logo_id: value,
    });
  }

  /**
   * This method will update the terminal_id on input change
   * @param {any} value
   */
  function onTerminalIdChange(value) {
    setBusinessCenterCompanyData({
      ...businessCenterCompanyData,
      terminal_id: value,
    });
  }

  /**
   * This method will navigate back to the previous page
   */
  function onCancel() {
    routeContext.back();
  }

  /**
   * This method will submit the form data to the API
   */
  async function onSubmit() {
    await callEvaApi(
      new EvaApiCall(
        business_center_company
          ? `business_center_companies/${business_center_company}`
          : `business_center_companies`
      )
        .setMethod(business_center_company ? "PUT" : "POST")
        .setData(businessCenterCompanyData)
        .setLoadingGroup("business_center_company_page_submit")
        .setErrorState(setSubmitError)
        .setRedirectOnSuccess(true)
    );
  }

  return (
    <BusinessCenterCompanyPageView
      business_center_company={business_center_company}
      businessCenterCompanyData={businessCenterCompanyData}
      companies={companies}
      logos={logos}
      terminals={terminals}
      onCompanyIdChange={onCompanyIdChange}
      onLogoIdChange={onLogoIdChange}
      onTerminalIdChange={onTerminalIdChange}
      onCancel={onCancel}
      onSubmit={onSubmit}
      submitError={submitError}
    />
  );
}

export default BusinessCenterCompanyPage;
